import { useState, useCallback, useRef, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { useOnlineStatus } from "@/hooks/useOnlineStatus";
import { postAgentTurn, agentErrorCode } from "@/lib/agent-request";
import { toast } from "sonner";

const SESSION_STORAGE_KEY = "checkmaty-agent-session";

/**
 * AI agent chat hook (TES-1128, WP4 of the AI-restore epic TES-1124).
 *
 * BACKEND CONTRACT (current, post-restore — see
 * `supabase/functions/_shared/agent-orchestrator/handler.ts`):
 *   POST {VITE_SUPABASE_URL}/functions/v1/agent-orchestrator
 *   user-JWT-scoped (Authorization: Bearer <access_token>, apikey: <anon>)
 *   body: { message, sessionId?, language }
 *   200 →  { sessionId, response, toolResults, usage }   (single JSON, NOT a stream)
 *   4xx/5xx → { error: string }
 *
 * The restored WP2 edge layer is **request/response**, not SSE. The earlier
 * Alt-Stand hook streamed `text-delta`/`tool-call-*` events against an older
 * contract; that path no longer exists. We therefore issue one JSON request
 * and surface a "thinking" placeholder (isStreaming) while it is in flight so
 * the UI still reads as a live assistant. `toolResults` from the response are
 * attached to the assistant message for the ActionFeedbackCard/ToolBadge UI.
 *
 * Image input: the current edge layer rejects images with HTTP 422
 * ("Image input is not supported by the restored WP2 edge layer yet"). We pass
 * images through and translate that 422 into a friendly capability message
 * rather than a raw error.
 */
export interface AgentChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: Date;
  toolResults?: ToolResult[];
  isStreaming?: boolean;
  activeToolCalls?: ToolCallEvent[];
  isError?: boolean;
  errorCode?: string;
  images?: Array<{ base64: string; mimeType: string }>;
}

export interface ToolResult {
  tool: string;
  success: boolean;
  result: Record<string, unknown>;
}

export interface ChatProgress {
  total: number;
  answered: number;
  percentage: number;
}

export interface ToolCallEvent {
  toolCallId: string;
  toolName: string;
}

interface AgentApiResponse {
  sessionId?: string;
  response?: string;
  toolResults?: ToolResult[];
  error?: string;
}

/**
 * Shape of the persisted `agent_sessions` row we read back on restore.
 * The table is DB-only (restored in migration 263) and not present in the
 * generated frontend `types.ts`, so the query is run through an untyped
 * builder and the result narrowed to this interface.
 */
interface AgentSessionRow {
  id: string;
  conversation_history: Array<{ role: string; content: string; timestamp: string }> | null;
  current_state: string | null;
  metadata: Record<string, unknown> | null;
  status: string;
  inspection_id: string | null;
}

/** Minimal untyped surface for the DB-only `agent_sessions` table. */
interface UntypedFrom {
  from: (table: string) => {
    select: (cols: string) => {
      eq: (
        col: string,
        val: string,
      ) => {
        single: () => Promise<{ data: AgentSessionRow | null; error: unknown }>;
      };
    };
  };
}

/** Apply a functional update to the last message in the array (immutable). */
function updateLastMessage(
  messages: AgentChatMessage[],
  updater: (msg: AgentChatMessage) => AgentChatMessage,
): AgentChatMessage[] {
  const updated = [...messages];
  if (updated.length > 0) {
    updated[updated.length - 1] = updater(updated[updated.length - 1]);
  }
  return updated;
}

/** Progress carried directly on a result or nested under result.data. */
function progressFromResult(result: Record<string, unknown>): ChatProgress | null {
  const direct = result.progress as ChatProgress | undefined;
  if (direct) return direct;
  const data = (result.data ?? {}) as Record<string, unknown>;
  return (data.progress as ChatProgress | undefined) ?? null;
}

/** Pull a ChatProgress object out of the first tool result that carries one. */
function extractProgress(toolResults: ToolResult[] | undefined): ChatProgress | null {
  const list = toolResults ?? [];
  for (const tr of list) {
    const found = progressFromResult(tr.result as Record<string, unknown>);
    if (found) return found;
  }
  return null;
}

/** Build the [userMessage, thinkingPlaceholder] pair appended on send. */
function buildTurnMessages(
  content: string,
  images: Array<{ base64: string; mimeType: string }> | undefined,
): AgentChatMessage[] {
  const hasImages = !!images && images.length > 0;
  return [
    {
      id: crypto.randomUUID(),
      role: "user",
      content,
      timestamp: new Date(),
      ...(hasImages ? { images } : {}),
    },
    {
      id: crypto.randomUUID(),
      role: "assistant",
      content: "",
      timestamp: new Date(),
      toolResults: [],
      isStreaming: true,
      activeToolCalls: [],
    },
  ];
}

/** Clear a leftover streaming flag on the trailing message (immutable). */
function clearTrailingStreaming(messages: AgentChatMessage[]): AgentChatMessage[] {
  const last = messages[messages.length - 1];
  if (!last?.isStreaming) return messages;
  return updateLastMessage(messages, (msg) => ({ ...msg, isStreaming: false }));
}

/** Map a restored conversation_history into chat messages. */
function restoredMessages(session: AgentSessionRow): AgentChatMessage[] {
  return (session.conversation_history ?? []).map((entry, idx) => ({
    id: `restored-${idx}`,
    role: entry.role as "user" | "assistant",
    content: entry.content,
    timestamp: new Date(entry.timestamp),
  }));
}

/** Read last_progress out of the session metadata, if present. */
function restoredProgress(session: AgentSessionRow): ChatProgress | null {
  const meta = (session.metadata ?? {}) as Record<string, unknown>;
  return (meta.last_progress as ChatProgress | undefined) ?? null;
}

export function useAgentChat() {
  const [messages, setMessages] = useState<AgentChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(() => {
    try {
      return sessionStorage.getItem(SESSION_STORAGE_KEY);
    } catch {
      return null;
    }
  });
  const [progress, setProgress] = useState<ChatProgress | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { i18n, t } = useTranslation();
  // Was a previously-persisted session already restored? Drives the restore-query's
  // `enabled` gate, so it must be render-visible state, not a ref (react-hooks/refs,
  // TES-1064). Behavior unchanged: set true after restore, reset to false in clearChat.
  const [hasRestored, setHasRestored] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const hasMountedRef = useRef(false);

  const { isOnline, wasOffline } = useOnlineStatus();

  // Cleanup any in-flight request on unmount/navigation.
  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  // "Back online" toast — mount guard avoids a false positive on first render.
  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }
    if (wasOffline) {
      toast.success(t("inspectionChat.offline.backOnline"));
    }
  }, [wasOffline, t]);

  // Restore the persisted session from the backend on mount (user-JWT-scoped,
  // RLS-protected, explicit columns per project pattern). queryFn stays pure.
  const { isLoading: isRestoring, data: restoredSession } = useQuery({
    queryKey: ["agent-chat-session", sessionId],
    queryFn: async (): Promise<AgentSessionRow | null> => {
      // `agent_sessions` is DB-only (migration 263) and not in the generated
      // frontend types → query through an untyped builder, then narrow.
      const { data: session } = await (supabase as unknown as UntypedFrom)
        .from("agent_sessions")
        .select("id, conversation_history, current_state, metadata, status, inspection_id")
        .eq("id", sessionId!)
        .single();

      if (!session || session.status !== "active") return null;
      return session;
    },
    enabled: !!sessionId && !hasRestored && messages.length === 0,
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });

  // Clear a stale sessionId when the backend reports no active session.
  const forgetSession = useCallback(() => {
    try {
      sessionStorage.removeItem(SESSION_STORAGE_KEY);
    } catch {
      /* ignore */
    }
    setSessionId(null);
  }, []);

  // Apply restored data on mount (runs even when served from cache).
  useEffect(() => {
    if (restoredSession === null) {
      forgetSession();
      return;
    }
    if (!restoredSession) return; // still loading / undefined

    const restored = restoredMessages(restoredSession);
    if (restored.length > 0) setMessages(restored);

    const progress = restoredProgress(restoredSession);
    if (progress) setProgress(progress);

    setHasRestored(true);
  }, [restoredSession, forgetSession]);

  // Translate a mapped error code to a user-facing string (falls back to the
  // server-provided message, then a generic one).
  const errorText = useCallback(
    (code: string, serverMessage?: string): string => {
      const map: Record<string, string> = {
        not_authenticated: t("inspectionChat.errors.notAuthenticated"),
        no_provider: t("inspectionChat.errors.noProvider"),
        rate_limited: t("inspectionChat.errors.rateLimited"),
        image_not_supported: t("inspectionChat.errors.imageNotSupported"),
      };
      return map[code] ?? serverMessage ?? t("inspectionChat.errors.generic");
    },
    [t],
  );

  // Apply an error to the trailing assistant placeholder.
  const failLastMessage = useCallback((text: string, code?: string) => {
    setError(text);
    setMessages((prev) =>
      updateLastMessage(prev, (msg) => ({
        ...msg,
        isStreaming: false,
        isError: true,
        ...(code ? { errorCode: code } : {}),
        content: text,
      })),
    );
  }, []);

  const persistSessionId = useCallback((id: string) => {
    setSessionId(id);
    try {
      sessionStorage.setItem(SESSION_STORAGE_KEY, id);
    } catch {
      /* ignore */
    }
  }, []);

  const applySuccess = useCallback(
    (payload: AgentApiResponse) => {
      if (payload.sessionId) persistSessionId(payload.sessionId);
      const toolResults = payload.toolResults ?? [];
      setMessages((prev) =>
        updateLastMessage(prev, (msg) => ({
          ...msg,
          content: payload.response ?? "",
          toolResults,
          isStreaming: false,
          activeToolCalls: [],
        })),
      );
      const nextProgress = extractProgress(toolResults);
      if (nextProgress) setProgress(nextProgress);
    },
    [persistSessionId],
  );

  // Resolve a thrown error to a translated message, or null for intentional aborts.
  const caughtErrorText = useCallback(
    (err: unknown): string | null => {
      if ((err as Error)?.name === "AbortError") return null;
      return err instanceof Error ? err.message : t("inspectionChat.errors.generic");
    },
    [t],
  );

  const sendMessage = useCallback(
    async (content: string, images?: Array<{ base64: string; mimeType: string }>) => {
      setMessages((prev) => [...prev, ...buildTurnMessages(content, images)]);
      setIsLoading(true);
      setError(null);

      abortControllerRef.current?.abort();
      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      const body = {
        message: content,
        sessionId: sessionId || undefined,
        language: i18n.language || "de",
        ...(images && images.length > 0 ? { images } : {}),
      };

      try {
        const { ok, status, payload } = await postAgentTurn(
          "agent-orchestrator",
          body,
          abortController.signal,
        );
        if (ok) {
          applySuccess(payload);
        } else {
          const code = agentErrorCode(status);
          failLastMessage(errorText(code, payload.error), code);
        }
      } catch (err) {
        const text = caughtErrorText(err);
        if (text) failLastMessage(text);
      } finally {
        setIsLoading(false);
        setMessages(clearTrailingStreaming);
      }
    },
    [sessionId, i18n.language, errorText, failLastMessage, applySuccess, caughtErrorText],
  );

  const clearChat = useCallback(() => {
    abortControllerRef.current?.abort();
    setMessages([]);
    setSessionId(null);
    setProgress(null);
    setError(null);
    setHasRestored(false);
    try {
      sessionStorage.removeItem(SESSION_STORAGE_KEY);
    } catch {
      /* ignore */
    }
  }, []);

  const isStreaming = messages.some((m) => m.isStreaming === true);
  const sendEnabled = !isLoading && !isRestoring && isOnline;

  return {
    messages,
    isLoading,
    isRestoring,
    isStreaming,
    sendMessage,
    clearChat,
    sessionId,
    progress,
    error,
    sendEnabled,
    isOnline,
  };
}
